import * as React from 'react';

import {useSelector, useDispatch} from 'react-redux';
import useI18n from 'src/hooks/useI18n';

import {selectApplicantInformation, selectUserInfo} from 'src/selectors/crm';

import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {FormLabelSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {getApplicantInformation} from 'src/action-creators/crm';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {getBaseUrl, getApiPrefix} from 'src/utils/crm';
import css from './resume.css';
import type {MenuOption} from '@spaced-out/ui-design-system';


const ResumeCard = ({
  applicantId,
  jobId,
}: {
  applicantId: number;
  jobId?: string;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const {user: userInfo} = useSelector(selectUserInfo) ?? {};
  const [isLoading, setIsLoading] = React.useState(true);
  const applicantDetails = useSelector(selectApplicantInformation);

  const resumeData = applicantDetails?.resume;

  const downloadMenuOptions = [
    {
      key: 'downloadPdf',
      label: t('Download_PDF', 'Download PDF'),
    },
    {
      key: 'downloadOriginalResume',
      label: t('Download_Original', 'Download Original'),
    },
  ];

  React.useEffect(() => {
    if (!applicantDetails || applicantDetails.id !== applicantId) {
      setIsLoading(true);
      if (applicantId) {
        dispatch(getApplicantInformation(applicantId)).finally(() =>
          setIsLoading(false),
        );
      } else {
        console.error('Unable to find applicant');
      }
    } else {
      setIsLoading(false);
    }

    // Cleanup: reset loading state on unmount
    return () => {
      setIsLoading(true);
    };
  }, [dispatch, applicantId, applicantDetails]);

  const hasVersionHistory =
    applicantDetails?.candidateAttachments?.some(
      (attachment: any) => attachment.attachmentType === 'RESUME',
    ) ?? false;

  const updatedDate = React.useMemo(() => {
    if (!resumeData?.updatedOn) {
      return '';
    }

    try {
      const date = new Date(resumeData.updatedOn);
      if (isNaN(date.getTime())) {
        return '';
      }
      return date.toISOString().split('T')[0];
    } catch (e: any) {
      return '';
    }
  }, [resumeData?.updatedOn]);

  const resumeLink = React.useMemo(() => {
    if (!applicantDetails || applicantDetails.id !== applicantId) {
      return '';
    }
    const baseUrl = getBaseUrl(window.location.href);
    if (!baseUrl) {
      return '';
    }
    // @ts-expect-error - TS2551 - Property 'resumeFileKey' does not exist on type 'Candidate'. Did you mean 'resumeFilekey'?
    const resumeUrl = `${baseUrl}/trmapi/api/candidate/show-pdf-resume/${applicantDetails.id}/${applicantDetails.resumeFileKey}`;
    return resumeUrl;
  }, [applicantDetails, applicantId]);

  const isDataMismatch =
    applicantDetails && applicantDetails.id !== applicantId;

  if (isLoading || isDataMismatch) {
    return (
      <div className={css.loader}>
        <CircularLoader />
      </div>
    );
  }

  const handleDownloadOriginalResume = () => {
    const originalResumeURI = window.encodeURI(
      `${getApiPrefix(
        userInfo?.roleTitle ?? '',
      )}/api/candidate/download-original-resume/${applicantDetails.id}/${
        applicantDetails.name ||
        // @ts-expect-error - TS2339 - Property 'resumeFilepath' does not exist on type 'Resume'.
        resumeData.resumeFilepath ||
        // @ts-expect-error - TS2339 - Property 'resumeFilekey' does not exist on type 'Resume'.
        resumeData.resumeFilekey
      }?sendPDF=true`,
    );
    const link = document.createElement('a');
    link.href = originalResumeURI;
    link.setAttribute('download', ``);
    document.body?.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadPdf = async () => {
    const originalResumeURI = window.encodeURI(
      `${getApiPrefix(
        userInfo?.roleTitle ?? '',
      )}/api/candidate/download-original-resume/${applicantDetails.id}/${
        applicantDetails.name ||
        // @ts-expect-error - TS2339 - Property 'resumeFilepath' does not exist on type 'Resume'.
        resumeData.resumeFilepath ||
        // @ts-expect-error - TS2339 - Property 'resumeFilekey' does not exist on type 'Resume'.
        resumeData.resumeFilekey
      }?sendPDF=true`,
    );
    window.open(originalResumeURI, '_blank');
  };

  const handleDownloadClick = (option: MenuOption) => {
    if (option.key === 'downloadPdf') {
      handleDownloadPdf();
    }
    if (option.key === 'downloadOriginalResume') {
      handleDownloadOriginalResume();
    }
  };

  return (
    <div className={css.card}>
      <div className={css.cardContent}>
        <div className={css.contentHeader}>
          {hasVersionHistory ? (
            <>
              <div className={css.headerLabel}>
                <FormLabelSmall>
                  {`${t('Last_updated_on', 'Last updated on')} ${updatedDate}`}
                </FormLabelSmall>
              </div>

              <div>
                <ButtonDropdown
                  children={t('Download', 'Download')}
                  menu={{
                    options: downloadMenuOptions,
                    size: 'small',
                    classNames: {
                      option: css.buttonDropdownOption,
                    },
                  }}
                  anchorPosition={`bottom-end`}
                  onOptionSelect={handleDownloadClick}
                  size="small"
                  type="tertiary"
                />
              </div>
            </>
          ) : null}
        </div>
        <div className={css.resumeContainer}>
          {resumeLink ? (
            <div className={css.iframe}>
              <iframe
                title="resume-iframe"
                src={resumeLink}
                height={sizeFluid}
                width={sizeFluid}
              />
            </div>
          ) : (
            <EmptyState
              description={t(
                'No_resume_found_for_this_candidate',
                'No resume found for this candidate',
              )}
              imageVariant="data"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeCard;
